import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Icon, Modal } from '@/shared/ui';

import styles from './RequireMFAModal.module.scss';

type RequireMFAModalProps = {
  isOpen: boolean;
  organizationName: string;
  membersCount: number;
  isSaving: boolean;
  className?: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export const RequireMFAModal: FC<RequireMFAModalProps> = (props) => {
  const {
    isOpen,
    organizationName,
    membersCount,
    isSaving,
    className,
    onCancel,
    onConfirm,
  } = props;

  const { formatMessage } = useIntl();

  return (
    <Modal
      isOpen={isOpen}
      className={className}
      onCancel={isSaving ? undefined : onCancel}
      shouldCloseOnOverlayClick={!isSaving}
      dataSection="require-mfa-modal"
      title={formatMessage({
        id: 'companySettings.requireMFA.title',
        defaultMessage: 'Require two-step verification',
      })}
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" disabled={isSaving} onClick={onCancel}>
            <FormattedMessage id="global.cancel" defaultMessage="Cancel" />
          </Button>

          <Button loading={isSaving} onClick={onConfirm}>
            <FormattedMessage
              id="companySettings.requireMFA.confirm"
              defaultMessage="Require for everyone"
            />
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className="p2">
          <FormattedMessage
            id="companySettings.requireMFA.description"
            defaultMessage="All {membersCount} members of {organizationName} will need an authenticator app to sign in. Sessions stay active; the code is requested at the next sign in."
            values={{ membersCount, organizationName }}
          />
        </p>

        <div className={styles.warning}>
          <Icon className={styles.warningIcon} name="info2" size={20} />

          <p className="p3">
            <FormattedMessage
              id="companySettings.requireMFA.ownership"
              defaultMessage="Two-step verification belongs to the member, not to the organization. A member who loses their authenticator app resets it themselves from their profile or through Diagnocat support — owners cannot reset it for them."
            />
          </p>
        </div>
      </div>
    </Modal>
  );
};
